/* Shared, read-only Zapiet storefront lookup for the pickup page, map and order flow. */
(() => {
  if (window.BbZapiet) return;
  const endpoint = 'https://api.zapiet.com/v1.0/pickup/locations';
  const normalise = (value) => String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
  const inFlight = new Map();
  const fullDate = new Intl.DateTimeFormat('nl-NL', {
    weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC',
  });

  function formatPickupDay(date, slots) {
    const minutes = (value) => /^\d{1,2}:\d{2}$/.test(value || '')
      ? Number(value.split(':')[0]) * 60 + Number(value.split(':')[1]) : NaN;
    const valid = (slots || []).map(({ from, until }) => ({
      from, until, start: minutes(from), end: minutes(until),
    })).filter(({ start, end }) => Number.isFinite(start) && Number.isFinite(end) && end > start);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date || '') || !valid.length) return null;
    const first = valid.reduce((left, right) => right.start < left.start ? right : left);
    const last = valid.reduce((left, right) => right.end > left.end ? right : left);
    const day = fullDate.format(new Date(`${date}T12:00:00Z`));
    const time = `${first.from}–${last.until} uur`;
    return { day, time, full: `${day} · ${time}` };
  }

  async function request(url, payload) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 10000);
    try {
      const response = await fetch(url, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: payload, credentials: 'omit', signal: controller.signal,
      });
      if (!response.ok) throw new Error(`Zapiet ${response.status}`);
      return await response.json();
    } finally { clearTimeout(timeout); }
  }

  function post(url, body) {
    const payload = JSON.stringify(body);
    const key = `${url}\n${payload}`;
    if (inFlight.has(key)) return inFlight.get(key);
    const promise = request(url, payload);
    inFlight.set(key, promise);
    const forget = () => {
      if (inFlight.get(key) === promise) inFlight.delete(key);
    };
    promise.then(forget, forget);
    return promise;
  }

  async function locationsFor(shop, product) {
    const shoppingCart = product ? [{ product_id: Number(product.product_id), variant_id: Number(product.variant_id), quantity: 1 }] : [];
    const result = await post(`${endpoint}?shop=${encodeURIComponent(shop)}&limit=200`, { shoppingCart });
    return Array.isArray(result.locations) ? result.locations : [];
  }

  async function matchingLocations(shop, products) {
    if (!products.length) return [];
    const responses = await Promise.all(products.map((product) => locationsFor(shop, product)));
    return responses[0].filter((location) => responses.every((list) => list.some((item) => String(item.id) === String(location.id))));
  }

  function resolve(matches, configuredId, collectionTitle, sharedProduct) {
    if (configuredId) return matches.find((item) => String(item.id) === String(configuredId)) || null;
    if (sharedProduct || matches.length !== 1) return null;
    const city = normalise(matches[0].city);
    const title = normalise(collectionTitle);
    if (!city || !title.startsWith(`${city} `)) return null;
    const locationName = normalise(matches[0].company_name);
    if (!locationName.startsWith(`${city} `)) return null;
    const titlePlace = title.slice(city.length).trim();
    const zapietPlace = locationName.slice(city.length).trim();
    if (titlePlace && titlePlace === zapietPlace) return matches[0];
    const titleWords = titlePlace.split(' ').filter((word) => word.length >= 5);
    const zapietWords = zapietPlace.split(' ').filter((word) => word.length >= 5);
    return titleWords.some((word) => zapietWords.some((candidate) => candidate.includes(word) || word.includes(candidate)))
      ? matches[0] : null;
  }

  function allowedDates(calendar) {
    const disabled = calendar?.disabled;
    if (!Array.isArray(disabled) || disabled[0] !== true) return [];
    const dates = disabled.slice(1).map((entry) => {
      if (typeof entry === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(entry)) return entry;
      if (Array.isArray(entry) && entry.length === 3 && entry.every(Number.isInteger)) {
        return new Date(Date.UTC(entry[0], entry[1], entry[2])).toISOString().slice(0, 10);
      }
      return null;
    });
    return dates.every(Boolean) ? dates : [];
  }

  async function calendarDates(shop, locationId, products) {
    const url = `${endpoint}/${locationId}/calendar?shop=${encodeURIComponent(shop)}`;
    const calendars = await Promise.all(products.map((product) => post(url, { shoppingCart: [product] })));
    const dates = calendars.map(allowedDates);
    if (dates.some((list) => !list.length)) return [];
    const minimum = calendars.map((calendar) => calendar.minDate || '').sort().at(-1);
    return [...new Set(dates[0])].filter((date) => date >= minimum && dates.every((list) => list.includes(date))).sort();
  }

  async function slotsForDate(shop, locationId, products, date) {
    const url = `${endpoint}/${locationId}/calendar/${date}?shop=${encodeURIComponent(shop)}`;
    const slots = await Promise.all(products.map((product) => post(url, { shoppingCart: [product] })));
    if (slots.some((list) => !Array.isArray(list))) return [];
    return slots[0].filter((slot) =>
      /^\d{1,2}:\d{2}$/.test(slot.available_from || '') &&
      slots.every((list) => list.some((other) => other.available_from === slot.available_from && other.available_until === slot.available_until)),
    ).map((slot) => ({ from: slot.available_from, until: slot.available_until }));
  }

  async function pickupSchedule(shop, locationId, products, limit = Infinity) {
    if (!products.length) return [];
    const selectedDates = (await calendarDates(shop, locationId, products)).slice(0, limit);
    const schedule = [];
    for (let start = 0; start < selectedDates.length; start += 4) {
      const batch = await Promise.all(selectedDates.slice(start, start + 4).map(async (date) => {
        const slots = await slotsForDate(shop, locationId, products, date);
        return slots.length ? { date, slots } : null;
      }));
      schedule.push(...batch.filter(Boolean));
    }
    return schedule;
  }

  async function pickupDays(shop, locationId, products) {
    return (await pickupSchedule(shop, locationId, products, 8)).map(({ date }) => date);
  }

  async function firstPickupDay(shop, locationId, products) {
    if (!products.length) return null;
    // Short checks need the first confirmed day, not the full visible schedule.
    // Keep the eight-candidate boundary, but stop requesting times after the first match.
    const dates = (await calendarDates(shop, locationId, products)).slice(0, 8);
    for (const date of dates) {
      if ((await slotsForDate(shop, locationId, products, date)).length) return date;
    }
    return null;
  }

  window.BbZapiet = { post, locationsFor, matchingLocations, resolve, pickupDays, firstPickupDay, pickupSchedule, formatPickupDay, normalise };
})();
